import {
  getAppIdentifierByOrgType,
  getFrontendUrlByOrgType,
  type AppIdentifier,
  type OrganizationType,
} from '../config/apps'
/**
 * Lógica pura de resolução da organização ativa e de roteamento entre portais.
 *
 * Tudo aqui é livre de `window`/`storage` (recebe os sinais por parâmetro), para
 * ser testável em ambiente node. Os wrappers que leem o DOM/storage ficam em
 * `auth.ts` (resolução da org ativa) e em `OrgSwitcher.tsx` (abertura em nova aba).
 */
import type { Membership } from '../types'

// Portal identifier -> tipos de org esperados naquele portal
export const PORTAL_ORG_TYPES: Record<string, string[]> = {
  ec: ['end_customer'],
  cp: ['cloud_provider'],
  rs: ['reseller'],
  adm: ['system', 'cloud_provider', 'reseller', 'end_customer'],
  hub: ['system', 'cloud_provider', 'reseller'],
}

/** Memberships cujo tipo de org o portal atende (`PORTAL_ORG_TYPES`). */
export const compatibleMemberships = (
  memberships: Membership[],
  appIdentifier: string,
): Membership[] => {
  const expectedTypes = PORTAL_ORG_TYPES[appIdentifier] || []
  return memberships.filter(
    (m) => m.organization_type && expectedTypes.includes(m.organization_type),
  )
}

/**
 * Membership que entra sem perguntar nada.
 * - uma só compatível com o portal -> ela;
 * - mais de uma -> `undefined`: a escolha é do usuário (#1145), nunca da ordem da lista
 *   nem de `is_default`;
 * - nenhuma compatível -> comportamento anterior (is_default, depois a primeira).
 */
export const findBestMembership = (
  memberships: Membership[],
  appIdentifier: string,
): Membership | undefined => {
  const compatible = compatibleMemberships(memberships, appIdentifier)
  if (compatible.length === 1) return compatible[0]
  if (compatible.length > 1) return undefined
  return memberships.find((m) => m.is_default) || memberships[0]
}

export type ActiveOrgSignals = {
  /** ?org=<id> da URL — sinal explícito ao abrir uma org em nova aba */
  urlOrg?: string | null
  /** org ativa desta aba (sessionStorage), sobrevive a F5 */
  sessionOrg?: string | null
  /** valor em memória desta sessão da store. O blob legado do localStorage NÃO entra aqui (#1145) */
  fallbackId?: string | null
  appIdentifier: string
}

/**
 * Resolve a membership ativa respeitando a prioridade por-aba:
 * ?org= > sessionStorage > fallback em memória > findBestMembership (que não decide
 * quando há mais de uma org compatível: ver `needsOrgChoice`).
 * IDs que não correspondem a nenhuma membership são ignorados.
 */
export const resolveActiveMembership = (
  memberships: Membership[],
  { urlOrg, sessionOrg, fallbackId, appIdentifier }: ActiveOrgSignals,
): Membership | undefined => {
  const find = (id?: string | null) =>
    id ? memberships.find((m) => m.organization_id === id) : undefined
  return (
    find(urlOrg) ||
    find(sessionOrg) ||
    find(fallbackId) ||
    findBestMembership(memberships, appIdentifier)
  )
}

/**
 * A tela "Em qual organização você quer atuar?" deve aparecer?
 * Sim quando há mais de uma membership compatível com o portal e nenhum sinal explícito
 * da aba (?org= ou sessionStorage, ambos validados contra as memberships) (a escolha feita na tela é gravada no sessionStorage). Sem "lembrar minha escolha": o blob legado não conta.
 */
export const needsOrgChoice = (
  memberships: Membership[],
  { urlOrg, sessionOrg, appIdentifier }: ActiveOrgSignals,
): boolean => {
  if (compatibleMemberships(memberships, appIdentifier).length <= 1) return false
  const known = (id?: string | null) => !!id && memberships.some((m) => m.organization_id === id)
  return !(known(urlOrg) || known(sessionOrg))
}

/**
 * Override por-aba aplicado na reidratação da store: usa ?org= / sessionStorage
 * somente se forem memberships válidas; caso contrário mantém o valor persistido.
 */
export const pickPerTabOrgOverride = (
  memberships: Membership[],
  signals: { urlOrg?: string | null; sessionOrg?: string | null },
): string | undefined => {
  const find = (id?: string | null) =>
    id && memberships.some((m) => m.organization_id === id) ? id : undefined
  return find(signals.urlOrg) || find(signals.sessionOrg) || undefined
}

const normalizeOrgType = (orgType?: string): OrganizationType | null => {
  if (
    orgType === 'system' ||
    orgType === 'cloud_provider' ||
    orgType === 'reseller' ||
    orgType === 'end_customer'
  ) {
    return orgType
  }
  return null
}

const normalizeBaseUrl = (url?: string): string | null => {
  if (!url) return null
  try {
    return new URL(url).origin
  } catch {
    return null
  }
}

export type OrgTargetContext = {
  /** window.location.origin da aba atual */
  origin: string
  /** identificador do portal atual (getCurrentAppIdentifier) */
  currentApp: AppIdentifier
  /** resolve a URL do portal por tipo de org; default usa a config global (lê window) */
  frontendUrlForOrgType?: (orgType: OrganizationType) => string
}

/**
 * Base URL do portal/domínio que atende a organização-alvo.
 * - Mesmo portal: domínio custom do reseller, se houver; senão a origem atual.
 * - Portal diferente: domínio custom, se houver; senão a URL do portal pelo tipo.
 * - Tipo desconhecido: permanece na origem atual.
 */
export const resolveOrgTargetBaseUrl = (
  m: Pick<Membership, 'organization_type' | 'frontend_url'>,
  ctx: OrgTargetContext,
): string => {
  const orgType = normalizeOrgType(m.organization_type)
  if (!orgType) return ctx.origin

  const custom = normalizeBaseUrl(m.frontend_url)
  const targetApp = getAppIdentifierByOrgType(orgType)
  const frontendUrlForOrgType = ctx.frontendUrlForOrgType ?? getFrontendUrlByOrgType

  if (ctx.currentApp === targetApp) {
    return custom && custom !== ctx.origin ? custom : ctx.origin
  }
  return custom || frontendUrlForOrgType(orgType)
}

/**
 * URL de destino para abrir uma organização em nova aba.
 * Anexa ?org=<id> para que a nova aba ative exatamente essa org (ver auth store).
 */
export const buildOrgTargetUrl = (
  m: Pick<Membership, 'organization_id' | 'organization_type' | 'frontend_url'>,
  ctx: OrgTargetContext,
): string => {
  const url = new URL('/dashboard', resolveOrgTargetBaseUrl(m, ctx))
  url.searchParams.set('org', m.organization_id)
  return url.toString()
}
